import { Drop, Fire, Lightning, Thermometer } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import type { TenantApartment } from "@/api/tenant/types";
import { Card } from "@/components/ui/card";
import { useHideElectricity } from "@/hooks/use-hide-electricity";
import { cn } from "@/lib/utils";

type RateCardProps = {
	icon: React.ReactNode;
	label: string;
	value: number | null;
	tone: string;
	className?: string;
};

function RateCard({ icon, label, value, tone, className }: RateCardProps) {
	return (
		<Card
			className={cn(
				"flex flex-col gap-3 border-border/60 p-4 sm:p-5",
				className,
			)}
		>
			<div className="flex items-center justify-between gap-2">
				<span
					className={cn(
						"flex size-9 items-center justify-center rounded-lg",
						tone,
					)}
					aria-hidden="true"
				>
					{icon}
				</span>
			</div>
			<div className="flex flex-col gap-0.5">
				<p className="text-xs font-medium text-muted-foreground">{label}</p>
				<p className="text-2xl font-semibold tabular-nums tracking-tight">
					{value != null ? value.toFixed(2) : "—"}
				</p>
			</div>
		</Card>
	);
}

type TenantStatCardsProps = {
	apartment: TenantApartment;
	itemClassName?: string;
};

export function TenantStatCards({
	apartment,
	itemClassName,
}: TenantStatCardsProps) {
	const { t } = useTranslation();
	const hideElectricity = useHideElectricity();

	return (
		<>
			<RateCard
				icon={<Drop weight="fill" className="size-5" />}
				label={t("tenant.rates.coldWater")}
				value={apartment.cold_water_rate}
				tone="bg-sky-500/10 text-sky-600 dark:text-sky-400"
				className={itemClassName}
			/>
			<RateCard
				icon={<Thermometer weight="fill" className="size-5" />}
				label={t("tenant.rates.hotWater")}
				value={apartment.hot_water_rate}
				tone="bg-orange-500/10 text-orange-600 dark:text-orange-400"
				className={itemClassName}
			/>
			<RateCard
				icon={<Fire weight="fill" className="size-5" />}
				label={t("tenant.rates.heat")}
				value={apartment.heat_rate}
				tone="bg-rose-500/10 text-rose-600 dark:text-rose-400"
				className={itemClassName}
			/>
			{!hideElectricity && (
				<RateCard
					icon={<Lightning weight="fill" className="size-5" />}
					label={t("tenant.rates.electricity")}
					value={apartment.consumption_rate}
					tone="bg-amber-500/10 text-amber-600 dark:text-amber-400"
					className={itemClassName}
				/>
			)}
		</>
	);
}
